import { Navigate, useLocation } from 'react-router-dom';
import { type ReactNode } from 'react';
import { Spinner } from '@musleempay/ui';
import { useAuth } from '../../hooks/useAuth.js';
import { homePathFor } from '../../lib/home-path.js';

/**
 * Route guard.
 *
 * Note what it does NOT do: it never decides what the user may see beyond
 * "signed in or not". Fine-grained authorisation lives on the server; the
 * client only avoids rendering a screen it knows will 403.
 */

export function RequireAuth({ children }: { children?: ReactNode }): JSX.Element {
  const { isAuthenticated, isLoading, session } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center" role="status" aria-live="polite">
        <Spinner className="h-8 w-8 text-teal-700" />
        <span className="sr-only">Checking your session</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // A merchant mid-onboarding is sent back to finish it, except when they are
  // already on that page.
  const merchant = session?.merchant;
  const needsOnboarding =
    merchant !== null &&
    merchant !== undefined &&
    merchant.onboardingStep !== 'COMPLETED' &&
    location.pathname !== '/onboarding' &&
    // An operator looking at a store is not its owner: they must be able to see
    // a half-set-up store as it is, not be pushed through its setup wizard.
    !session?.supportAccess;

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

/**
 * Inverse guard for public-only screens — currently just the marketing page
 * at "/". A merchant who is already signed in and lands on the marketing
 * site (e.g. bookmarked app.musleempay.com/) is sent straight to the
 * dashboard instead of seeing a "Start free" page meant for prospects.
 *
 * Deliberately simpler than `RequireAuth`: it skips the onboarding check,
 * because a merchant mid-onboarding is still "signed in" and `/dashboard`
 * will route them onward via `RequireAuth` itself.
 */
export function RedirectIfAuthenticated({ children }: { children?: ReactNode }): JSX.Element {
  const { isAuthenticated, isLoading, session } = useAuth();

  if (isLoading) {
    return <>{children}</>;
  }

  if (isAuthenticated && session) {
    return <Navigate to={homePathFor(session)} replace />;
  }

  return <>{children}</>;
}

/**
 * Keeps the platform area to platform operators.
 *
 * Wrap this INSIDE `RequireAuth` (which handles loading and signed-out). It is
 * a convenience, not the security boundary: it only spares a merchant a screen
 * full of 403s. Every `/api/v1/admin` route enforces the same rule itself.
 */
export function RequirePlatformAdmin({ children }: { children?: ReactNode }): JSX.Element {
  const { session } = useAuth();

  if (!session?.isPlatformAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

/**
 * Keeps store-only pages away from a platform operator who has no store.
 *
 * Every page behind this shows or edits one merchant's data, and its API calls
 * fail with "Finish setting up your store" for someone attached to none. An
 * operator needs no store — they work from the platform area — so rather than
 * let them land on a page of errors (by a bookmark, a back button, or being
 * returned to where they were headed before signing in), send them home.
 *
 * Wrap this INSIDE `RequireAuth` and OUTSIDE the layout, so the layout's own
 * requests never fire. An operator who also runs a store has a merchant and
 * passes straight through.
 */
export function RequireStore({ children }: { children?: ReactNode }): JSX.Element {
  const { session } = useAuth();

  if (session?.isPlatformAdmin && session.merchant === null) {
    return <Navigate to={homePathFor(session)} replace />;
  }

  return <>{children}</>;
}
